import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API_URL, formatError, getBearerToken } from "../lib/api.js";
import {
  Car, Ship, FileText, Hash, MapPin, CheckCircle2, AlertTriangle, Loader2,
  ArrowLeft, History, Upload, Plus, Trash2, Camera, ClipboardCheck,
  Wrench, Users, Bell, X, Download,
} from "lucide-react";
import PageHeader from "../components/PageHeader.jsx";

/**
 * Página única da viatura (identificada por VIN) — Fases 1 + 2 + 3.
 */
export default function VehiclePage() {
  const { vin } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview");
  const [enums, setEnums] = useState(null);

  const load = async () => {
    try { setData((await api.get(`/vehicles/${vin}`)).data); }
    catch (e) { setError(formatError(e.response?.data?.detail) || "Falha a carregar"); }
  };
  useEffect(() => { load(); api.get("/logistics/docs-enums").then(r => setEnums(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, [vin]);

  if (error) return <div className="p-8 text-center text-rose-600">{error}</div>;
  if (!data) return <div className="p-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> A carregar…</div>;

  const { vehicle, logistics, vr, timeline } = data;
  const active = logistics.find((l) => l.is_active);

  const tabs = [
    { k: "overview",    label: "Resumo",      Icon: Car },
    { k: "documents",   label: "Documentos",  Icon: FileText },
    { k: "inspections", label: "Inspecções",  Icon: ClipboardCheck },
    { k: "issues",      label: "Danos",       Icon: AlertTriangle },
    { k: "locations",   label: "Localização", Icon: MapPin },
    { k: "related",     label: "Ligações",    Icon: Users },
    { k: "timeline",    label: "Timeline",    Icon: History },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-screen-xl mx-auto">
      <Link to="/logistica" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-ronil-navy mb-3" data-testid="back-to-logistics">
        <ArrowLeft className="w-3.5 h-3.5" /> Logística
      </Link>

      <PageHeader
        overline={<span className="font-mono">{vehicle.vin}</span>}
        title={`${vehicle.brand || "Viatura"} ${vehicle.model || ""} ${vehicle.version || ""}`.trim()}
        subtitle="Página única da viatura — toda a informação organizada por área."
      />

      <div className="flex items-center gap-1 flex-wrap mb-5 border-b border-slate-200">
        {tabs.map((t) => {
          const Icon = t.Icon; const active = tab === t.k;
          return (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`vehicle-tab-${t.k}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-heading font-semibold border-b-2 transition-all -mb-px ${active ? "border-ronil-navy text-ronil-navy" : "border-transparent text-slate-500 hover:text-ronil-navy"}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview"    && <OverviewTab vehicle={vehicle} active={active} history={logistics.filter(l => !l.is_active)} vr={vr} />}
      {tab === "documents"   && <DocumentsTab vin={vehicle.vin} enums={enums} />}
      {tab === "inspections" && <InspectionsTab vin={vehicle.vin} enums={enums} />}
      {tab === "issues"      && <IssuesTab vin={vehicle.vin} enums={enums} />}
      {tab === "locations"   && <LocationsTab vin={vehicle.vin} />}
      {tab === "related"     && <RelatedTab vin={vehicle.vin} />}
      {tab === "timeline"    && <TimelineTab timeline={timeline} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function OverviewTab({ vehicle, active, history, vr }) {
  return (
    <>
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card title="Identificação" Icon={Car}>
          <Row label="VIN"       value={<span className="font-mono">{vehicle.vin}</span>} />
          <Row label="Marca"     value={vehicle.brand} />
          <Row label="Modelo"    value={vehicle.model} />
          <Row label="Versão"    value={vehicle.version} />
          <Row label="Ano/MY"    value={vehicle.model_year} />
          <Row label="Nº motor"  value={vehicle.engine_number} />
        </Card>
        <Card title="Especificações" Icon={FileText}>
          <Row label="Cor exterior" value={vehicle.exterior_color} />
          <Row label="Cor interior" value={vehicle.interior_color} />
          <Row label="Combustível"  value={vehicle.fuel} />
          <Row label="Transmissão"  value={vehicle.transmission} />
        </Card>
        <Card title="Processos Ligados" Icon={Hash}>
          <Row label="Logística activa" value={active ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Sim</span> : <span className="text-slate-500">Nenhuma</span>} />
          <Row label="Processos antigos" value={history.length ? `${history.length}` : "0"} />
          <Row label="VR associado" value={vr ? <Link className="text-ronil-navy underline" to={`/vr/${vr.id}`}>{vr.registration_plate || vr.registration_number || vr.id?.slice(0, 8)}</Link> : <span className="text-slate-500">Nenhum</span>} />
        </Card>
      </section>

      {active && (
        <section className="bg-white border border-slate-200 rounded-md p-5 mb-6 shadow-soft-card" data-testid="active-logistics">
          <header className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-heading font-semibold">Processo logístico activo</div>
              <h3 className="font-display font-bold text-xl text-ronil-navy">{active.status}</h3>
            </div>
            {!active.workflow_complete && <span className="text-xs text-rose-600 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Workflow incompleto</span>}
          </header>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <h4 className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Origem</h4>
              <Row label="País" value={active.origin_country} />
              <Row label="Porto" value={active.origin_port} />
              <Row label="Navio" value={active.ship} />
              <Row label="Contentor" value={active.container} />
              <Row label="Transportadora" value={active.carrier} />
            </div>
            <div>
              <h4 className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Datas</h4>
              <Row label="Embarque" value={active.departure_date} />
              <Row label="ETA" value={active.eta} />
              <Row label="Chegada real" value={active.actual_arrival_date} />
              <Row label="Descarga" value={active.unloading_date} />
              <Row label="Dias no estado" value={<span className="font-mono tabular-nums">{active.days_in_status ?? "—"}</span>} />
            </div>
            <div>
              <h4 className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Responsabilidade</h4>
              <Row label="Dept." value={active.responsible_department} />
              <Row label="Dealer destino" value={active.destination_dealer} />
              <Row label="Próxima acção" value={active.next_action} />
              <Row label="Data limite" value={active.deadline} />
              {active.blocked_reason && <Row label="Bloqueio" value={<span className="text-rose-600">{active.blocked_reason}</span>} />}
            </div>
          </div>
          {active.w7_entry_date && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded px-3 py-2 text-sm">
              <div className="text-[10px] uppercase tracking-widest text-amber-800 font-heading font-bold">W7</div>
              <Row label="Entrada W7" value={active.w7_entry_date} />
              <Row label="Dias no W7" value={<span className="font-mono tabular-nums">{active.days_in_w7 ?? "—"}</span>} />
              <Row label="Estado aduaneiro" value={active.w7_customs_status} />
              <Row label="Previsão libertação" value={active.w7_expected_release_date} />
              <Row label="Saída real" value={active.w7_actual_release_date} />
            </div>
          )}
        </section>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function DocumentsTab({ vin, enums }) {
  const [docs, setDocs] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const load = async () => { try { setDocs((await api.get(`/vehicles/${vin}/documents`)).data); } catch { setDocs([]); } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vin]);

  return (
    <div data-testid="documents-tab">
      <SectionHeader title="Documentos" Icon={FileText} action={<button onClick={() => setShowForm(true)} className="bg-ronil-navy text-white px-3 py-1.5 rounded-md text-xs font-heading font-semibold inline-flex items-center gap-1" data-testid="upload-doc-btn"><Upload className="w-3.5 h-3.5" /> Carregar documento</button>} />
      {docs === null ? <Spinner /> : docs.length === 0 ? <Empty label="Sem documentos carregados." /> : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
              <tr><th className="px-3 py-2 text-left">Tipo</th><th className="px-3 py-2 text-left">Referência</th><th className="px-3 py-2 text-left">Data</th><th className="px-3 py-2 text-left">Ficheiro</th><th className="px-3 py-2 text-left">Carregado por</th><th className="px-3 py-2 text-right">Acções</th></tr>
            </thead>
            <tbody>
              {docs.map(d => (
                <tr key={d.id} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 text-xs">{d.document_type}</td>
                  <td className="px-3 py-1.5 text-xs font-mono">{d.reference || "—"}</td>
                  <td className="px-3 py-1.5 text-xs">{d.issue_date || "—"}</td>
                  <td className="px-3 py-1.5 text-xs">
                    <a href={`${API_URL}/vehicle-documents/${d.id}/download`} target="_blank" rel="noreferrer" className="text-ronil-navy hover:underline">{d.original_filename || d.id.slice(0, 8)}</a>
                  </td>
                  <td className="px-3 py-1.5 text-xs">{d.uploaded_by_name}</td>
                  <td className="px-3 py-1.5 text-xs text-right">
                    <button onClick={async () => { if (!window.confirm("Apagar este documento?")) return; await api.delete(`/vehicle-documents/${d.id}`); load(); }} className="text-rose-600 hover:text-rose-700 inline-flex items-center gap-0.5 text-xs"><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showForm && enums && <UploadDocDialog vin={vin} enums={enums} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function UploadDocDialog({ vin, enums, onClose, onSaved }) {
  const [docType, setDocType] = useState(enums.document_types[0]);
  const [reference, setReference] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [notes, setNotes] = useState("");
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const f = fileRef.current?.files?.[0];
    if (!f) { setErr("Selecciona um ficheiro"); return; }
    setBusy(true); setErr("");
    const fd = new FormData();
    fd.append("document_type", docType);
    if (reference) fd.append("reference", reference);
    if (issueDate) fd.append("issue_date", issueDate);
    if (notes) fd.append("notes", notes);
    fd.append("file", f);
    try { await api.post(`/vehicles/${vin}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } }); onSaved(); }
    catch (ex) { setErr(formatError(ex.response?.data?.detail) || "Falha ao carregar"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog onClose={onClose} title="Carregar documento" testid="upload-doc-dialog">
      <form onSubmit={submit} className="p-5 grid gap-3 text-sm">
        <Field label="Tipo *"><select className={inputCls} value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="doc-type-select">{enums.document_types.map(t => <option key={t} value={t}>{t}</option>)}</select></Field>
        <Field label="Referência / Nº"><input className={inputCls} value={reference} onChange={(e) => setReference(e.target.value)} /></Field>
        <Field label="Data de emissão"><input type="date" className={inputCls} value={issueDate} onChange={(e) => setIssueDate(e.target.value)} /></Field>
        <Field label="Notas"><textarea rows={2} className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
        <Field label="Ficheiro *"><input type="file" ref={fileRef} className="text-sm" data-testid="doc-file-input" /></Field>
        {err && <ErrorBanner msg={err} />}
        <DialogFooter busy={busy} onClose={onClose} testid="doc-submit" />
      </form>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function InspectionsTab({ vin, enums }) {
  const [items, setItems] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const load = async () => { try { setItems((await api.get(`/vehicles/${vin}/inspections`)).data); } catch { setItems([]); } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vin]);

  return (
    <div data-testid="inspections-tab">
      <SectionHeader title="Inspecções físicas" Icon={ClipboardCheck} action={<button onClick={() => setShowForm(true)} className="bg-ronil-navy text-white px-3 py-1.5 rounded-md text-xs font-heading font-semibold inline-flex items-center gap-1" data-testid="new-inspection-btn"><Plus className="w-3.5 h-3.5" /> Nova inspecção</button>} />
      {items === null ? <Spinner /> : items.length === 0 ? <Empty label="Sem inspecções registadas." /> : (
        <div className="space-y-3">
          {items.map(i => (
            <article key={i.id} className="bg-white border border-slate-200 rounded-md p-4">
              <header className="flex items-center justify-between mb-2 flex-wrap gap-1">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">{(i.inspection_date || i.created_at || "").slice(0, 10)} · {i.created_by_name}</div>
                  <h4 className="font-heading font-bold text-ronil-navy">{i.result}</h4>
                </div>
                <PhotoUploader endpoint={`/vehicle-inspections/${i.id}/photos`} onDone={load} testid={`insp-upload-${i.id}`} />
              </header>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-slate-600">
                <div><span className="text-slate-400">KM: </span>{i.mileage_km ?? "—"}</div>
                <div><span className="text-slate-400">Combustível: </span>{i.fuel_level_pct ? `${i.fuel_level_pct}%` : "—"}</div>
                <div className="col-span-2"><span className="text-slate-400">Observações: </span>{i.observations || "—"}</div>
              </div>
              {i.photos?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {i.photos.map(p => (
                    <a key={p.id} href={`${API_URL}/vehicle-inspection-photos/${p.id}/download`} target="_blank" rel="noreferrer" className="w-20 h-20 border border-slate-200 rounded overflow-hidden bg-slate-50 inline-flex items-center justify-center text-[10px] text-slate-500 hover:border-ronil-navy">
                      <Camera className="w-4 h-4" />
                    </a>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>
      )}
      {showForm && enums && <InspectionDialog vin={vin} enums={enums} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function InspectionDialog({ vin, enums, onClose, onSaved }) {
  const [form, setForm] = useState({ result: enums.inspection_results[0], mileage_km: "", fuel_level_pct: "", observations: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = { result: form.result, observations: form.observations };
      if (form.mileage_km) payload.mileage_km = parseInt(form.mileage_km, 10);
      if (form.fuel_level_pct) payload.fuel_level_pct = parseInt(form.fuel_level_pct, 10);
      await api.post(`/vehicles/${vin}/inspections`, payload); onSaved();
    } catch (ex) { setErr(formatError(ex.response?.data?.detail) || "Falha a guardar"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog onClose={onClose} title="Nova inspecção" testid="inspection-dialog">
      <form onSubmit={submit} className="p-5 grid gap-3 text-sm">
        <Field label="Resultado *"><select className={inputCls} value={form.result} onChange={(e) => setForm({ ...form, result: e.target.value })} data-testid="insp-result">{enums.inspection_results.map(r => <option key={r} value={r}>{r}</option>)}</select></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="KM"><input type="number" className={inputCls} value={form.mileage_km} onChange={(e) => setForm({ ...form, mileage_km: e.target.value })} /></Field>
          <Field label="Combustível (%)"><input type="number" min="0" max="100" className={inputCls} value={form.fuel_level_pct} onChange={(e) => setForm({ ...form, fuel_level_pct: e.target.value })} /></Field>
        </div>
        <Field label="Observações"><textarea rows={3} className={inputCls} value={form.observations} onChange={(e) => setForm({ ...form, observations: e.target.value })} /></Field>
        {err && <ErrorBanner msg={err} />}
        <DialogFooter busy={busy} onClose={onClose} testid="insp-submit" />
      </form>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function IssuesTab({ vin, enums }) {
  const [items, setItems] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const load = async () => { try { setItems((await api.get(`/vehicles/${vin}/issues`)).data); } catch { setItems([]); } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vin]);

  return (
    <div data-testid="issues-tab">
      <SectionHeader title="Danos / não-conformidades" Icon={AlertTriangle} action={<button onClick={() => setShowForm(true)} className="bg-ronil-navy text-white px-3 py-1.5 rounded-md text-xs font-heading font-semibold inline-flex items-center gap-1" data-testid="new-issue-btn"><Plus className="w-3.5 h-3.5" /> Registar dano</button>} />
      {items === null ? <Spinner /> : items.length === 0 ? <Empty label="Sem danos registados." /> : (
        <div className="space-y-3">
          {items.map(i => <IssueCard key={i.id} issue={i} enums={enums} onChanged={load} />)}
        </div>
      )}
      {showForm && enums && <IssueDialog vin={vin} enums={enums} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function IssueCard({ issue, enums, onChanged }) {
  const [editStatus, setEditStatus] = useState(false);
  const [newStatus, setNewStatus] = useState(issue.status);
  const [comment, setComment] = useState("");
  const sevColor = { "Leve": "bg-slate-100 text-slate-700", "Médio": "bg-amber-100 text-amber-800", "Grave": "bg-rose-100 text-rose-800" }[issue.severity] || "bg-slate-100";
  const save = async () => {
    try { await api.patch(`/vehicle-issues/${issue.id}`, { status: newStatus, comment }); setEditStatus(false); setComment(""); onChanged(); } catch (e) { alert(formatError(e.response?.data?.detail)); }
  };
  return (
    <article className="bg-white border border-slate-200 rounded-md p-4" data-testid={`issue-${issue.id}`}>
      <header className="flex items-start gap-3 mb-2">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">{(issue.created_at || "").slice(0, 10)} · {issue.created_by_name}</div>
          <h4 className="font-heading font-bold text-ronil-navy">{issue.title}</h4>
          <div className="text-xs text-slate-500">{issue.location_on_vehicle || "—"}</div>
        </div>
        <span className={`inline-flex px-2 py-0.5 rounded-sm text-[10px] font-heading font-bold uppercase tracking-wider ${sevColor}`}>{issue.severity}</span>
        <span className="inline-flex px-2 py-0.5 rounded-sm text-[10px] font-heading font-bold uppercase tracking-wider bg-slate-100 text-slate-700">{issue.category}</span>
      </header>
      {issue.description && <p className="text-sm text-slate-600 mb-2">{issue.description}</p>}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs">
          <span className="text-slate-400">Estado: </span>
          <span className="font-heading font-semibold">{issue.status}</span>
          {issue.deadline && <span className="text-slate-500 ml-2">· prazo {issue.deadline}</span>}
        </div>
        <div className="flex items-center gap-2">
          <PhotoUploader endpoint={`/vehicle-issues/${issue.id}/photos`} onDone={onChanged} testid={`issue-upload-${issue.id}`} />
          <button onClick={() => setEditStatus(!editStatus)} className="text-xs text-ronil-navy hover:underline">Actualizar</button>
        </div>
      </div>
      {editStatus && (
        <div className="mt-3 bg-slate-50 border border-slate-200 rounded p-2 flex gap-2 flex-wrap items-end">
          <Field label="Novo estado"><select className={inputCls} value={newStatus} onChange={(e) => setNewStatus(e.target.value)} data-testid={`issue-status-${issue.id}`}>{enums.issue_statuses.map(s => <option key={s} value={s}>{s}</option>)}</select></Field>
          <Field label="Comentário"><input className={inputCls} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Opcional" /></Field>
          <button onClick={save} className="bg-ronil-navy text-white rounded px-3 py-1.5 text-xs font-heading font-semibold">Guardar</button>
        </div>
      )}
      {issue.photos?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {issue.photos.map(p => (
            <a key={p.id} href={`${API_URL}/vehicle-issue-photos/${p.id}/download`} target="_blank" rel="noreferrer" className="w-20 h-20 border border-slate-200 rounded overflow-hidden bg-slate-50 inline-flex items-center justify-center text-[10px] text-slate-500 hover:border-ronil-navy">
              <Camera className="w-4 h-4" />
            </a>
          ))}
        </div>
      )}
      {issue.timeline?.length > 1 && (
        <details className="mt-3 text-xs text-slate-500">
          <summary className="cursor-pointer hover:text-ronil-navy">Histórico ({issue.timeline.length})</summary>
          <ol className="mt-2 border-l-2 border-slate-200 pl-3 space-y-1">
            {issue.timeline.map((t, idx) => <li key={idx}><span className="text-slate-400">{(t.at || "").slice(0, 16).replace("T", " ")}</span> · {t.event}{t.comment && <> — {t.comment}</>}</li>)}
          </ol>
        </details>
      )}
    </article>
  );
}

function IssueDialog({ vin, enums, onClose, onSaved }) {
  const [form, setForm] = useState({ category: enums.issue_categories[0], severity: "Leve", title: "", description: "", location_on_vehicle: "", deadline: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setErr("Título obrigatório"); return; }
    setBusy(true); setErr("");
    try { await api.post(`/vehicles/${vin}/issues`, Object.fromEntries(Object.entries(form).filter(([_, v]) => v !== ""))); onSaved(); }
    catch (ex) { setErr(formatError(ex.response?.data?.detail) || "Falha a guardar"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog onClose={onClose} title="Registar dano / não-conformidade" testid="issue-dialog">
      <form onSubmit={submit} className="p-5 grid gap-3 text-sm">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Categoria *"><select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="issue-category">{enums.issue_categories.map(c => <option key={c} value={c}>{c}</option>)}</select></Field>
          <Field label="Severidade *"><select className={inputCls} value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} data-testid="issue-severity">{enums.issue_severities.map(c => <option key={c} value={c}>{c}</option>)}</select></Field>
        </div>
        <Field label="Título *"><input required className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="issue-title" /></Field>
        <Field label="Localização na viatura"><input className={inputCls} value={form.location_on_vehicle} onChange={(e) => setForm({ ...form, location_on_vehicle: e.target.value })} /></Field>
        <Field label="Descrição"><textarea rows={3} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
        <Field label="Prazo"><input type="date" className={inputCls} value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} /></Field>
        {err && <ErrorBanner msg={err} />}
        <DialogFooter busy={busy} onClose={onClose} testid="issue-submit" />
      </form>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function LocationsTab({ vin }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ kind: "Parque Ronil", site: "", zone: "", row: "", spot: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const load = async () => { try { setItems((await api.get(`/vehicles/${vin}/locations`)).data); } catch { setItems([]); } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vin]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/vehicles/${vin}/locations`, form); setForm({ kind: "Parque Ronil", site: "", zone: "", row: "", spot: "", notes: "" }); load(); }
    finally { setBusy(false); }
  };
  const KINDS = ["Porto", "W7", "Parque externo", "Parque Ronil", "Oficina", "PDI", "Showroom", "Dealer"];
  return (
    <div data-testid="locations-tab">
      <SectionHeader title="Histórico de localizações" Icon={MapPin} />
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-md p-4 mb-4 grid grid-cols-2 md:grid-cols-6 gap-2 text-xs items-end">
        <Field label="Tipo"><select className={inputCls} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="loc-kind">{KINDS.map(k => <option key={k} value={k}>{k}</option>)}</select></Field>
        <Field label="Local"><input className={inputCls} value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} /></Field>
        <Field label="Zona"><input className={inputCls} value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} /></Field>
        <Field label="Fila"><input className={inputCls} value={form.row} onChange={(e) => setForm({ ...form, row: e.target.value })} /></Field>
        <Field label="Lugar"><input className={inputCls} value={form.spot} onChange={(e) => setForm({ ...form, spot: e.target.value })} /></Field>
        <button disabled={busy} className="bg-ronil-navy text-white rounded-md px-3 py-1.5 text-xs font-heading font-semibold disabled:opacity-50" data-testid="loc-submit">{busy ? "A guardar…" : "Registar"}</button>
      </form>
      {items === null ? <Spinner /> : items.length === 0 ? <Empty label="Sem registos de localização." /> : (
        <ol className="relative border-l-2 border-slate-200 pl-5 space-y-3">
          {items.map(e => (
            <li key={e.id} className="relative">
              <span className="absolute -left-[23px] top-1 w-3 h-3 rounded-full bg-ronil-navy border-2 border-white"></span>
              <div className="text-[11px] text-slate-500">{(e.at || "").replace("T", " ").slice(0, 16)} · {e.user_name}</div>
              <div className="text-sm text-slate-800"><strong>{e.kind}</strong>{e.site && <> · {e.site}</>}{e.zone && <> · {e.zone}</>}{e.row && <> · fila {e.row}</>}{e.spot && <> · {e.spot}</>}</div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function RelatedTab({ vin }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/vehicles/${vin}/related`).then(r => setData(r.data)).catch(() => setData({})); }, [vin]);
  if (!data) return <Spinner />;
  return (
    <div data-testid="related-tab" className="space-y-5">
      <section>
        <h3 className="font-heading font-bold text-ronil-navy mb-2 text-sm uppercase tracking-widest inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Cliente</h3>
        {data.customer ? (
          <div className="bg-white border border-slate-200 rounded-md p-4 text-sm">
            <div className="font-heading font-bold">{data.customer.name}</div>
            <div className="text-xs text-slate-500 font-mono">{data.customer.customer_number}</div>
            <div className="text-xs mt-1">{data.customer.phone} · {data.customer.email}</div>
            <Link to={`/customers/${data.customer.id}`} className="text-xs text-ronil-navy hover:underline">Ver ficha completa →</Link>
          </div>
        ) : <Empty label="Nenhum cliente atribuído a este VIN." />}
      </section>

      <LinkedList title="Oficina (SOM)" Icon={Wrench} items={data.service_cases} empty="Sem casos de serviço." linkFn={s => `/service-cases/${s.id}`} labelFn={s => `${s.number} · ${s.status} · ${s.service_advisor_name || "—"}`} />
      <LinkedList title="Carroçaria (BPS)" Icon={Wrench} items={data.body_shop_cases} empty="Sem casos de carroçaria." linkFn={s => `/body-shop-cases/${s.id}`} labelFn={s => `${s.number} · ${s.workshop_state || s.admin_state || "—"}`} />
      <LinkedList title="Oportunidades" Icon={Users} items={data.opportunities} empty="Sem oportunidades CRM." linkFn={() => "/opportunities"} labelFn={o => `${o.number} · ${o.current_state} · ${o.subject || "—"}`} />
      <LinkedList title="Reclamações" Icon={AlertTriangle} items={data.complaints} empty="Sem reclamações." linkFn={() => "/complaints"} labelFn={c => `${c.number} · ${c.current_state} · ${c.category || "—"}`} />
    </div>
  );
}

function LinkedList({ title, Icon, items, empty, linkFn, labelFn }) {
  return (
    <section>
      <h3 className="font-heading font-bold text-ronil-navy mb-2 text-sm uppercase tracking-widest inline-flex items-center gap-1"><Icon className="w-3.5 h-3.5" /> {title} ({items?.length || 0})</h3>
      {!items || items.length === 0 ? <Empty label={empty} /> : (
        <ul className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
          {items.map(it => <li key={it.id} className="px-3 py-2 text-sm"><Link to={linkFn(it)} className="text-ronil-navy hover:underline">{labelFn(it)}</Link></li>)}
        </ul>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function TimelineTab({ timeline }) {
  return (
    <div data-testid="vehicle-timeline">
      {timeline.length === 0 ? <Empty label="Sem eventos ainda." /> : (
        <ol className="relative border-l-2 border-slate-200 pl-5 space-y-3">
          {timeline.map((e) => (
            <li key={e.id} className="relative">
              <span className="absolute -left-[23px] top-1 w-3 h-3 rounded-full bg-ronil-navy border-2 border-white"></span>
              <div className="text-[11px] text-slate-500">{fmt(e.at)} · <span className="font-heading font-semibold text-slate-700">{e.user_name || "—"}</span></div>
              <div className="text-sm text-slate-800">{e.event} {e.old_status && e.new_status && <>: <span className="text-slate-500">{e.old_status}</span> → <strong>{e.new_status}</strong></>}</div>
              {e.comment && <div className="text-xs text-slate-500 italic">{e.comment}</div>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared UI primitives

function PhotoUploader({ endpoint, onDone, testid }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const onChange = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } }); onDone(); } catch (_) {}
    finally { setBusy(false); ref.current.value = ""; }
  };
  return (
    <label className="inline-flex items-center gap-1 text-xs text-ronil-navy hover:underline cursor-pointer" data-testid={testid}>
      <Camera className="w-3.5 h-3.5" /> {busy ? "A enviar…" : "Foto"}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={onChange} />
    </label>
  );
}

function SectionHeader({ title, Icon, action }) {
  return (
    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
      <h3 className="font-heading font-bold text-ronil-navy text-sm uppercase tracking-widest inline-flex items-center gap-1"><Icon className="w-3.5 h-3.5" /> {title}</h3>
      {action}
    </div>
  );
}

function Dialog({ title, onClose, testid, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md shadow-xl w-full max-w-xl max-h-[92vh] overflow-y-auto" data-testid={testid}>
        <header className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-ronil-navy">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </header>
        {children}
      </div>
    </div>
  );
}

function DialogFooter({ busy, onClose, testid }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-md hover:bg-slate-50">Cancelar</button>
      <button type="submit" disabled={busy} className="px-4 py-2 text-sm bg-ronil-navy text-white rounded-md hover:bg-ronil-navy-hover disabled:opacity-50" data-testid={testid}>{busy ? "A guardar…" : "Guardar"}</button>
    </div>
  );
}

function ErrorBanner({ msg }) { return <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2">{msg}</div>; }
function Spinner() { return <div className="p-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> A carregar…</div>; }
function Empty({ label }) { return <div className="bg-white border border-dashed border-slate-300 rounded-md p-8 text-center text-slate-400 text-sm">{label}</div>; }

function Card({ title, Icon, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 shadow-soft-card">
      <h3 className="text-[10px] uppercase tracking-widest font-heading font-bold text-ronil-navy mb-2 inline-flex items-center gap-1"><Icon className="w-3.5 h-3.5" /> {title}</h3>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <span className="text-[11px] uppercase tracking-widest text-slate-400 min-w-[90px] font-heading font-semibold">{label}</span>
      <span className="text-slate-700 flex-1">{value ?? <span className="text-slate-300">—</span>}</span>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-widest font-heading font-semibold text-slate-500">{label}</span>
      {children}
    </label>
  );
}

const inputCls = "w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm focus:border-ronil-navy focus:outline-none focus:ring-2 focus:ring-ronil-navy/10";

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
